import * as THREE from 'three'

// Non-destructive per-material colour adjustment. Injects a uniform-driven
// HSV+contrast+tint chunk into the standard shader so the *source* texture is
// never mutated — slide the values, see the change, no re-upload, fully
// reversible by sliding back to defaults.
//
// Fields read from material_assets.data:
//   textureTint        [r,g,b] (default [1,1,1])  — multiplied with diffuse
//   textureContrast    number  (default 1)        — 0 = flat grey, 2 = punchy
//   textureSaturation  number  (default 1)        — 0 = grayscale, 2 = oversat
//   textureValue       number  (default 1)        — final RGB multiplier
//
// The chunk is *always* installed (not skipped on identity values) so the
// inspector can live-update the uniforms without forcing a recompile every
// time a slider crosses the identity threshold. Per-pixel cost: ~4 mults.
//
// Live update path:
//   applyTextureAdjust(mat, d)      — install at build time, seeds uniforms.
//   setTextureAdjustUniform(mat, k, v)
//                                   — mutate one uniform's .value in place.
//                                     Avatar re-renders next frame; no PATCH,
//                                     no shader recompile.

export function applyTextureAdjust(mat, d) {
  const tint = Array.isArray(d.textureTint) && d.textureTint.length === 3 ? d.textureTint : [1, 1, 1]
  const contrast = d.textureContrast ?? 1
  const saturation = d.textureSaturation ?? 1
  const value = d.textureValue ?? 1

  // Persist uniform refs on userData so live mutation works post-compile.
  const uniforms = {
    uTexTint:       { value: new THREE.Vector3(tint[0], tint[1], tint[2]) },
    uTexContrast:   { value: contrast },
    uTexSaturation: { value: saturation },
    uTexValue:      { value: value },
  }
  mat.userData.__texAdjustUniforms = uniforms

  mat.onBeforeCompile = (shader) => {
    shader.uniforms.uTexTint = uniforms.uTexTint
    shader.uniforms.uTexContrast = uniforms.uTexContrast
    shader.uniforms.uTexSaturation = uniforms.uTexSaturation
    shader.uniforms.uTexValue = uniforms.uTexValue
    shader.fragmentShader = shader.fragmentShader
      .replace(
        '#include <common>',
        `#include <common>
uniform vec3 uTexTint;
uniform float uTexContrast;
uniform float uTexSaturation;
uniform float uTexValue;
vec3 _aosTexAdjust(vec3 c) {
  c *= uTexTint;
  float g = dot(c, vec3(0.299, 0.587, 0.114));
  c = mix(vec3(g), c, uTexSaturation);
  c = (c - 0.5) * uTexContrast + 0.5;
  c *= uTexValue;
  return clamp(c, 0.0, 1.0);
}`
      )
      .replace(
        '#include <map_fragment>',
        `#include <map_fragment>
diffuseColor.rgb = _aosTexAdjust(diffuseColor.rgb);`
      )
  }
  // Fixed cache key so all materials share one compiled program — uniforms
  // are per-material; the shader is identical.
  mat.customProgramCacheKey = () => 'aos-tex-adjust:v1'
  mat.needsUpdate = true
}

// Live update one of the four uniforms without rebuilding the material.
// Returns true if the update was applied, false if the material wasn't set
// up with the adjustment chunk (e.g. legacy / freshly built without the
// onBeforeCompile hook).
export function setTextureAdjustUniform(mat, key, value) {
  const u = mat.userData?.__texAdjustUniforms
  if (!u) return false
  if (key === 'textureTint' && Array.isArray(value) && value.length === 3) {
    u.uTexTint.value.set(value[0], value[1], value[2])
    return true
  }
  if (key === 'textureContrast')   { u.uTexContrast.value = value; return true }
  if (key === 'textureSaturation') { u.uTexSaturation.value = value; return true }
  if (key === 'textureValue')      { u.uTexValue.value = value; return true }
  return false
}
